import React from 'react';
import { FormattedMessage } from 'react-intl';
import messages from '../../../../../text/messages';
import Background from '../../../../../assets/images/intro.jpg';

// intro steps
import AcknowledgmentStep from './intro/acknowledgement';
import ChooseTypeStep from './intro/chooseType';

// employee steps
import PersonalInformationStep from './employee/personalInformation';
import OfferLetterStep from './employee/offerLetter';
import AssignmentLetterStep from './employee/assignmentLetter';
import EmployeeArbitrationStep from './employee/employeeArbitration';
import BackgroundCheckFCRAStep from './employee/backgroundCheckFCRA';
import BackgroundCheckDRBStep from './employee/backgroundCheckDRB';
import BackgroundCheckCDRBStep from './employee/backgroundCheckCDRB';
import BackgroundCheckAuthorizationStep from './employee/backgroundCheckAuthorization';
import BackgroundCheckAdditionalInfoStep from './employee/backgroundCheckAdditionalInfo';

import EmployeeHandbookStep from './employee/employeeHandbook';

import { afterBGSteps, BGSteps, icSteps, icStepsContinuation } from '../../../../../utilities/regime';

export function getSteps(controls, progress, props) {
    const { branch } = progress;
    let intro = getIntroSteps(controls, progress, props);

    switch (branch) {
        case 'w2':
            return [...intro, ...getEmployeeSteps(controls, progress, props, intro.length)];

        case 'ic':
        case 'ic-continue-request':
            return [...intro, ...getContractorInitialSteps(controls, progress, props, intro.length)];

        case 'ic-continue':
            return getContractorContinuationSteps(controls, progress, props);

        case 'default':
        default:
            return intro;
    }
}

function getIntroSteps(controls, progress, props) {
    const { next } = controls;
    const { profile, job, client, preferContractType } = props;
    const { branch } = progress;
    let rest = { ...props, ...controls };

    const { classify_client_pref, classify_client_result, classify_result } = job;
    if (classify_result === 'w2' || classify_client_result === 'w2-only' || classify_client_pref === 'w2-only') {
        return [
            <AcknowledgmentStep
                headerImage={Background}
                hideProgress
                hideMenu
                onSubmit={() => next(1, job.id, 'w2')}
                title={<FormattedMessage id={'job.agreementTitle'} defaultMessage={'Welcome {name}!'} values={{ name: profile.first_name }} />}
                {...rest}
            />
        ];
    } else {
        return [
            <AcknowledgmentStep
                headerImage={Background}
                hideProgress
                hideMenu
                onSubmit={() => next(1, job.id, branch)}
                title={<FormattedMessage id={'job.agreementTitle'} defaultMessage={'Welcome {name}!'} values={{ name: profile.first_name }} />}
                {...rest}
            />,
            <ChooseTypeStep
                onSubmit={values => preferContractType(job, values.contractType, () => next(2, job.id, values.contractType))}
                title={<FormattedMessage id={'job.chooseTypeTitle'} defaultMessage={'How do you prefer to be paid?'} values={{ company: client.name }} />}
                {...rest}
            />
        ];
    }
}

function getBGStepsTitle(progress, total) {
    return <FormattedMessage {...messages.backgroundCheckProgress} values={{ progress, total }} />;
}

function getBGSteps(controls, progress, props, offset = 0) {
    const { client, job, address, addresses } = props;
    const { bg_check, bg_check_type } = client.job_policies || {};
    const rest = { ...props, ...controls };
    const moveTo = to => () => controls.next(offset + to, job.id, progress.branch);

    if (!bg_check || job.bg_check === 'waived') return [];

    const workerAddresses = addresses || [{}];
    const workerAddress = workerAddresses[workerAddresses.length - 1];

    const isCA =
        (job.work_location_type === 'onsite' && address && address.state === 'CA') ||
        (job.work_location_type === 'offsite' && workerAddress && workerAddress.state === 'CA');

    const includeAdditionalInfo = bg_check && job.bg_check !== 'waived' && bg_check_type === 'enhanced';

    const bgStepNumber = {};
    bgStepNumber.FCRA = 1;
    bgStepNumber.DRB = bgStepNumber.FCRA + 1;
    bgStepNumber.CDRB = bgStepNumber.DRB + (isCA ? 1 : 0);
    bgStepNumber.AdditionalInfo = bgStepNumber.CDRB + (includeAdditionalInfo ? 1 : 0);
    bgStepNumber.Authorize = bgStepNumber.AdditionalInfo + 1;
    bgStepNumber.total = bgStepNumber.Authorize;
    const bgStepsCA = isCA
        ? [<BackgroundCheckCDRBStep onSubmit={moveTo(bgStepNumber.CDRB)} title={getBGStepsTitle(bgStepNumber.CDRB, bgStepNumber.total)} {...rest} hidden />]
        : [];

    const bgStepsAdditional = includeAdditionalInfo
        ? [
              <BackgroundCheckAdditionalInfoStep
                  onSubmit={moveTo(bgStepNumber.AdditionalInfo)}
                  title={getBGStepsTitle(bgStepNumber.AdditionalInfo, bgStepNumber.total)}
                  {...rest}
                  hidden
              />
          ]
        : [];

    return [
        <BackgroundCheckFCRAStep onSubmit={moveTo(bgStepNumber.FCRA)} title={getBGStepsTitle(bgStepNumber.FCRA, bgStepNumber.total)} {...rest} />,
        <BackgroundCheckDRBStep onSubmit={moveTo(bgStepNumber.DRB)} title={getBGStepsTitle(bgStepNumber.DRB, bgStepNumber.total)} {...rest} hidden />,
        ...bgStepsCA,
        ...bgStepsAdditional,
        <BackgroundCheckAuthorizationStep
            onSubmit={moveTo(bgStepNumber.Authorize)}
            title={getBGStepsTitle(bgStepNumber.Authorize, bgStepNumber.total)}
            {...rest}
            hidden
        />
    ];
}

function getAfterBGsteps(controls, progress, props, offset) {
    const { job } = props;
    const rest = { ...props, ...controls };
    const moveTo = to => () => controls.next(offset + to, job.id, progress.branch);
    return afterBGSteps(moveTo, rest);
}

function getEmployeeSteps(controls, progress, props, offset = 0) {
    const { job } = props;
    const rest = { ...props, ...controls };
    const moveTo = to => () => controls.next(offset + to, job.id, progress.branch);

    const beforeBGsteps = [
        <PersonalInformationStep onSubmit={moveTo(1)} message={messages.personalInformation} {...rest} />,
        <OfferLetterStep onSubmit={moveTo(2)} message={messages.offerLetter} {...rest} />,
        <AssignmentLetterStep onSubmit={moveTo(3)} message={messages.assignmentLetter} {...rest} />, // 2
        <EmployeeArbitrationStep onSubmit={moveTo(4)} message={messages.signatureAndArbitration} {...rest} />,
        <EmployeeHandbookStep onSubmit={moveTo(5)} message={messages.employeeHandbook} {...rest} />
    ];

    const bgSteps = BGSteps(props.regime, getBGSteps(controls, progress, props, offset + beforeBGsteps.length));
    const afterBGsteps = getAfterBGsteps(controls, progress, props, offset + beforeBGsteps.length + bgSteps.length);

    return [...beforeBGsteps, ...bgSteps, ...afterBGsteps];
}

function getContractorInitialSteps(controls, progress, props, offset = 0) {
    let rest = { ...props, ...controls };
    let moveTo = (to, branch = progress.branch) => () => controls.next(offset + to, props.job.id, branch);
    return icSteps(moveTo, rest);
}

function getContractorContinuationSteps(controls, progress, props, offset = 0) {
    let rest = { ...props, ...controls };
    let moveTo = to => () => controls.next(offset + to, props.job.id, progress.branch);

    return icStepsContinuation(moveTo, rest);
}



// WEBPACK FOOTER //
// ./src/pages/onboarding/tenants/contractor/steps/index.jsx